import { useTranslation } from "react-i18next";
import { errorText } from "../../utils/apiError";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  InputBase,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useMemo, useState } from "react";
import { showcaseApi } from "../../http/showcase";
import { formatTimecode, type ShowcaseVideo } from "@shared/showcase";
import {
  SHOWCASE_CAPTION_LABELS,
  type ShowcaseCaptionState,
  type ShowcaseCaptionTrack,
  type ShowcaseCue,
} from "@shared/showcaseCaptions";

/**
 * Auto captions + chapters, the review side (services/showcaseCaptions.ts).
 *
 * Everything generated arrives as a DRAFT. A track reaches the watch page only
 * when someone presses "Publish" here; suggested chapters and the summary are
 * applied with their own explicit button. Editing a line and publishing writes
 * a fresh WebVTT file, so a correction is live within the minute.
 */

/** The cues with a person's edits applied; a cleared line is removed. */
export function applyEdits(
  cues: ShowcaseCue[],
  edits: Record<number, string>
): ShowcaseCue[] {
  return cues.flatMap((c, i) => {
    const text = (i in edits ? edits[i] : c.text).replace(/\s+/g, " ").trim();
    return text ? [{ ...c, text }] : [];
  });
}

function TrackEditor({
  videoId,
  track,
  onState,
  onSeek,
}: {
  videoId: string;
  track: ShowcaseCaptionTrack;
  onState: (s: ShowcaseCaptionState) => void;
  onSeek: (t: number) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A new draft (regenerate) or a save resets local edits.
  useEffect(() => setEdits({}), [track.updatedAt]);

  const dirty = Object.keys(edits).length > 0;
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return track.cues
      .map((c, i) => ({ c, i }))
      .filter(
        ({ c, i }) => !q || (edits[i] ?? c.text).toLowerCase().includes(q)
      );
  }, [track.cues, edits, filter]);

  const save = async (approved: boolean) => {
    setBusy(true);
    setError(null);
    try {
      onState(
        await showcaseApi.saveCaptions(videoId, track.lang, {
          ...(dirty ? { cues: applyEdits(track.cues, edits) } : {}),
          approved,
        })
      );
    } catch (e) {
      setError(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight="fontWeightSemibold">
                {track.label}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={
                  track.origin === "transcribed"
                    ? t("captionsTab.spoken")
                    : t("captionsTab.translated")
                }
              />
              <Chip
                size="small"
                color={track.approved ? "success" : "default"}
                label={
                  track.approved
                    ? t("captionsTab.live")
                    : t("captionsTab.draft")
                }
                data-testid={`caption-state-${track.lang}`}
              />
              {track.edited && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("captionsTab.edited")}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              {dirty && track.approved && (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => setEdits({})}
                >
                  {t("captionsTab.discardChanges")}
                </Button>
              )}
              {dirty && !track.approved && (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => save(false)}
                >
                  {t("captionsTab.saveDraft")}
                </Button>
              )}
              {track.approved && !dirty ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => save(false)}
                >
                  {t("captionsTab.unpublish")}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={() => save(true)}
                  data-testid={`publish-captions-${track.lang}`}
                >
                  {track.approved
                    ? t("captionsTab.publishChanges")
                    : t("captionsTab.publish")}
                </Button>
              )}
            </Stack>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="small"
            placeholder={t("captionsTab.findWord")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              maxHeight: 420,
              overflowY: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            {shown.map(({ c, i }) => (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: i in edits ? "action.hover" : undefined,
                }}
              >
                <Button
                  size="small"
                  onClick={() => onSeek(c.start)}
                  sx={{
                    minWidth: 64,
                    fontFamily: "fontFamilyMono",
                    color: "text.secondary",
                  }}
                  title={t("captionsTab.playFromHere")}
                >
                  {formatTimecode(c.start)}
                </Button>
                <InputBase
                  fullWidth
                  multiline
                  value={edits[i] ?? c.text}
                  onChange={(e) =>
                    setEdits((cur) => {
                      const next = { ...cur };
                      if (e.target.value === c.text) delete next[i];
                      else next[i] = e.target.value;
                      return next;
                    })
                  }
                  inputProps={{
                    "aria-label": t("captionsTab.lineAt", {
                      time: formatTimecode(c.start),
                    }),
                  }}
                  sx={{ typography: "bodyMd" }}
                />
              </Stack>
            ))}
            {shown.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {t("captionsTab.noLineMatches")}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("captionsTab.linesHint", { count: track.cues.length })}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Suggestions({
  state,
  video,
  onVideo,
  onSeek,
}: {
  state: ShowcaseCaptionState;
  video: ShowcaseVideo;
  onVideo: (v: ShowcaseVideo) => void;
  onSeek: (t: number) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chapters = state.suggestedChapters;
  const summary = state.suggestedSummary;
  if (!chapters.length && !summary) return null;
  const sameChapters =
    JSON.stringify(chapters) === JSON.stringify(video.chapters);
  const apply = async (patch: Parameters<typeof showcaseApi.update>[1]) => {
    setBusy(true);
    setError(null);
    try {
      onVideo(await showcaseApi.update(video.id, patch));
    } catch (e) {
      setError(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography fontWeight="fontWeightSemibold">
            {t("captionsTab.suggested")}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {chapters.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t("captionsTab.chapters")}
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 0, listStyle: "none" }}>
                {chapters.map((c) => (
                  <Stack
                    component="li"
                    key={c.startSeconds}
                    direction="row"
                    spacing={1.5}
                    alignItems="baseline"
                  >
                    <Button
                      size="small"
                      onClick={() => onSeek(c.startSeconds)}
                      sx={{
                        minWidth: 56,
                        fontFamily: "fontFamilyMono",
                        color: "text.secondary",
                      }}
                    >
                      {formatTimecode(c.startSeconds)}
                    </Button>
                    <Typography variant="body2">{c.title}</Typography>
                  </Stack>
                ))}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy || sameChapters}
                  onClick={() => apply({ chapters })}
                  data-testid="use-chapters"
                >
                  {sameChapters
                    ? t("captionsTab.inUse")
                    : t("captionsTab.useChapters")}
                </Button>
                {!sameChapters && video.chapters.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t("captionsTab.replacesChapters", {
                      count: video.chapters.length,
                    })}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
          {summary && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t("captionsTab.summary")}
              </Typography>
              <Typography variant="body2">{summary}</Typography>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy || video.description === summary}
                  onClick={() => apply({ description: summary })}
                >
                  {video.description === summary
                    ? t("captionsTab.inUse")
                    : video.description
                      ? t("captionsTab.replaceAbout")
                      : t("captionsTab.useAsAbout")}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function CaptionsTab({
  video,
  onVideo,
  onSeek,
}: {
  video: ShowcaseVideo;
  onVideo: (v: ShowcaseVideo) => void;
  onSeek: (t: number) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [state, setState] = useState<ShowcaseCaptionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () =>
      showcaseApi
        .captions(video.id)
        .then((s) => {
          setState(s);
          setError(null);
        })
        .catch((e) => setError(errorText(e, t))),
    [video.id, t]
  );
  useEffect(() => {
    void load();
  }, [load, video.captionStatus, video.rev]);

  const working = state?.status === "queued" || state?.status === "running";
  useEffect(() => {
    if (!working) return;
    const t = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(t);
  }, [working, load]);

  const onState = useCallback(
    (s: ShowcaseCaptionState) => {
      setState(s);
      // The published list lives on the video; keep the page's copy current.
      void showcaseApi
        .get(video.id)
        .then(onVideo)
        .catch(() => {});
    },
    [video.id, onVideo]
  );

  const generate = async () => {
    setBusy(true);
    setConfirm(false);
    try {
      setState(await showcaseApi.generateCaptions(video.id));
      setError(null);
    } catch (e) {
      setError(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  if (!state)
    return error ? <Alert severity="error">{error}</Alert> : <LinearProgress />;

  const live = state.tracks.filter((t) => t.approved).length;

  return (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
            >
              <Box>
                <Typography fontWeight="fontWeightSemibold">
                  {t("captionsTab.heading")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {working
                    ? t("captionsTab.listening")
                    : state.status === "review"
                      ? state.tracks.length
                        ? state.spokenLang
                          ? t("captionsTab.reviewDetected", {
                              language:
                                SHOWCASE_CAPTION_LABELS[state.spokenLang],
                              live,
                              count: state.tracks.length,
                            })
                          : t("captionsTab.review", {
                              live,
                              count: state.tracks.length,
                            })
                        : t("captionsTab.nothingToCaption")
                      : state.status === "failed"
                        ? t("captionsTab.lastAttemptFailed")
                        : t("captionsTab.intro")}
                </Typography>
              </Box>
              {state.available && !working && (
                <Button
                  variant={state.status === "review" ? "outlined" : "contained"}
                  disabled={busy || !video.hlsUrl}
                  onClick={() =>
                    state.tracks.length ? setConfirm(true) : void generate()
                  }
                  data-testid="generate-captions"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {state.status === "none"
                    ? t("captionsTab.generate")
                    : state.status === "failed"
                      ? t("captionsTab.tryAgain")
                      : t("captionsTab.regenerate")}
                </Button>
              )}
            </Stack>
            {working && <LinearProgress />}
            {!state.available && state.unavailableReason && (
              <Alert severity="info">{state.unavailableReason}</Alert>
            )}
            {state.error && (
              <Alert severity={state.status === "failed" ? "error" : "warning"}>
                {state.error}
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      <Suggestions
        state={state}
        video={video}
        onVideo={onVideo}
        onSeek={onSeek}
      />

      {state.tracks.map((t) => (
        <TrackEditor
          key={t.lang}
          videoId={video.id}
          track={t}
          onState={onState}
          onSeek={onSeek}
        />
      ))}

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>{t("captionsTab.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("captionsTab.confirmBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button variant="contained" onClick={() => void generate()}>
            {t("captionsTab.regenerate")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
